import { Business } from "@turizi/shared";
import { BUSINESS_TEMPLATES } from "@/types/templates";
import { TagIcon } from "@/lib/tag-utils";
import {
    IconStar,
    IconMail,
    IconPhone,
    IconMapPin,
    IconFileDescription,
    IconCoins,
    IconDiscount,
    IconId
} from "@tabler/icons-react";
import { getAttrValue } from "@/lib/models-utils";
import { Badge, Button, CopyButton } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { SkeletonListWithFilters } from "./snippets";
import { useMemo } from "react";

interface Props {
    business: Business;
    isLoading?: boolean;
    className?: string;
}

export default function BusinessPageHeader({
    business,
    isLoading = false,
    className
}: Props): JSX.Element | null {
    const { t } = useTranslation();

    const primaryEmail = useMemo(() => {
        if (!business?.emails) return null;
        const values = Object.values(business.emails).filter(Boolean);
        return values.length > 0 ? values[0] : null;
    }, [business?.emails]);

    const primaryPhone = useMemo(() => {
        if (!business?.phones) return null;
        const values = Object.values(business.phones).filter(Boolean);
        return values.length > 0 ? values[0] : null;
    }, [business?.phones]);

    if (isLoading) {
        return (
            <div className={className || 'solid-box-border p-4'}>
                <SkeletonListWithFilters />
            </div>
        );
    }

    if (!business) {
        return null;
    }

    const isHotel = business.category === BUSINESS_TEMPLATES.hotel.category;
    const stars = isHotel ? getAttrValue(business, 'stars') : 0;
    const tenantBusiness = business.tenant_business;

    return (
        <div className={className || 'flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-6 md:gap-8 p-4 bg-gray-50 rounded-xl border border-gray-100 shadow-md'}>
            {/* Header Title & Badges Section (Left Aligned, No justify-between) */}
            <div className="flex items-start gap-3 min-w-0 w-full sm:w-auto">
                <div className="shrink-0 flex flex-col items-center">
                    <TagIcon
                        name={business.category}
                        size="md"
                        hideText
                    />
                    <span className="text-[10px] text-gray-400  tracking-tight mt-0.5">
                        #{business.id}
                    </span>
                </div>

                <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                            {t(business.category)}
                        </span>
                        {stars > 0 && (
                            <div className="flex items-center gap-0.5">
                                {Array.from({ length: stars }).map((_, i) => (
                                    <IconStar key={i} size={14} color="#f59e0b" fill="#f59e0b" stroke={1} />
                                ))}
                            </div>
                        )}
                    </div>

                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
                        {business.name}
                    </h1>

                    {business.tax_name && business.tax_name !== business.name && (
                        <p className="text-xs text-gray-500 font-normal italic truncate">
                            {business.tax_name}
                        </p>
                    )}
                </div>
            </div>

            {/* Quick Info bar (Emails, Phones, Address) */}
            {(primaryEmail || primaryPhone || business.tax_id_number || tenantBusiness?.default_currency || tenantBusiness?.discount_category || tenantBusiness?.is_favorite) && (
                <div className="text-xs flex flex-wrap gap-2 items-center w-full sm:w-auto">
                    {primaryEmail && (
                        <CopyButton value={primaryEmail}>
                            {({ copied, copy }) => (
                                <Button
                                    variant="light"
                                    color={copied ? 'teal' : 'gray'}
                                    size="xs"
                                    onClick={copy}
                                    leftSection={<IconMail size={14} className="text-gray-400 shrink-0" />}
                                    className="font-normal text-gray-700"
                                >
                                    {primaryEmail}
                                </Button>
                            )}
                        </CopyButton>
                    )}
                    {primaryPhone && (
                        <CopyButton value={primaryPhone}>
                            {({ copied, copy }) => (
                                <Button
                                    variant="light"
                                    color={copied ? 'teal' : 'gray'}
                                    size="xs"
                                    onClick={copy}
                                    leftSection={<IconPhone size={14} className="text-gray-400 shrink-0" />}
                                    className="font-normal text-gray-700"
                                >
                                    {primaryPhone}
                                </Button>
                            )}
                        </CopyButton>
                    )}
                    {business.tax_id_number && (
                        <CopyButton value={business.tax_id_number}>
                            {({ copied, copy }) => (
                                <Button
                                    variant="light"
                                    color={copied ? 'teal' : 'gray'}
                                    size="xs"
                                    onClick={copy}
                                    leftSection={<IconId size={14} className="text-gray-400 shrink-0" />}
                                    className="font-normal text-gray-700"
                                >
                                    {business.tax_id_number}
                                </Button>
                            )}
                        </CopyButton>
                    )}
                    {tenantBusiness?.default_currency && (
                        <Badge
                            color="blue"
                            variant="light"
                            size="lg"
                            leftSection={<IconCoins size={13} />}
                        >
                            {tenantBusiness.default_currency}
                        </Badge>
                    )}
                    {tenantBusiness?.discount_category && (
                        <Badge
                            color="green"
                            variant="light"
                            size="lg"
                            leftSection={<IconDiscount size={13} />}
                        >
                            {tenantBusiness.discount_category}
                        </Badge>
                    )}
                    {tenantBusiness?.is_favorite && (
                        <Badge color="yellow" variant="light" size="sm">
                            {t('favorite')}
                        </Badge>
                    )}
                </div>
            )}
        </div>
    );
}
